import { useCallback, useEffect, useState } from "react"
import { apiEndpoints } from "generated/apiEndpoints"
import { useApi } from "auth/useApi3"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"

const PAYSLIP_REQUEST_TIMEOUT_MS = 210000

export interface PayslipPdf {
  pdfStr: string
  employeeNo: string
  salaryPeriodName: string
  salaryPeriodPaymentDate: string
  netSalary: number
  grossSalary: number
  taxDeduction: number
  benefits: number
  finalized: boolean
}

const base64ToBlob = (base64: string): Blob | null => {
  const cleanBase64 = base64.replace(/^data:application\/pdf;base64,/, "")

  try {
    const byteCharacters = atob(cleanBase64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: "application/pdf" })
  } catch (error) {
    console.error("Invalid base64 string", error)
    return null
  }
}

const downloadPdf = (pdfData: string, filename: string) => {
  const blobPDF = base64ToBlob(pdfData)
  if (!blobPDF) return false

  const blobUrl = window.URL.createObjectURL(blobPDF)
  const downloadLink = document.createElement("a")

  downloadLink.href = blobUrl
  downloadLink.download = filename
  document.body.appendChild(downloadLink)
  downloadLink.click()

  document.body.removeChild(downloadLink)
  window.URL.revokeObjectURL(blobUrl)
  return true
}

export const usePayslip = () => {
  const [listPayslipsResult, listPayslipsApi] = useApi() as [IApiResult, IApiFunc]
  const [downloadFailed, setDownloadFailed] = useState(false)

  const loadPayslips = useCallback(() => {
    listPayslipsApi.get(apiEndpoints.payslip.listpayslips, { timeout: PAYSLIP_REQUEST_TIMEOUT_MS })
  }, [listPayslipsApi])

  const downloadPayslip = useCallback((payslip: PayslipPdf) => {
    setDownloadFailed(false)
    const filename = "Lonespec_" + (payslip?.salaryPeriodPaymentDate || payslip?.salaryPeriodName || "payslip")
    const downloaded = Boolean(payslip?.pdfStr) && downloadPdf(payslip.pdfStr, filename)
    if (!downloaded) setDownloadFailed(true)
  }, [])

  useEffect(() => {
    loadPayslips()
  }, [loadPayslips])

  return {
    listPayslipsResult,
    downloadFailed,
    downloadPayslip,
    loadPayslips
  }
}
